import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cx from 'classnames';
import { Icon, ButtonTooltip } from '@intellecteu/common-ui';
import { Labels, userCreatePath } from 'app/constants';
import { useNotifications, useRoles } from 'app/utils';
import { useDeleteUser, useGetUsers, useParticipantStatus, type UserDto, ParticipantDto } from 'data';
import { TableSkeleton } from 'ui';
import { ui } from 'ui/styles';
import { Modal } from '../../modals';
import { PageSubCaption } from '../../ui/sub-caption';
import { Table, useTableFilteringRows } from '../../ui/table';
import { useUsersTableColumns } from './cols';

import s from './styles.module.scss';

interface UsersProps {
  participantName: string;
  port: number;
  phase?: ParticipantDto.phase;
  isRemote?: boolean;
  apiVersion: string;
  /** When set, participant has a validator parent (no Local Parties tab, no filter on Parties). */
  validatorParent?: string | null;
}

export const Users = ({ participantName, port, phase, isRemote = false, apiVersion, validatorParent }: UsersProps) => {
  const navigate = useNavigate();
  const { data: healthStatus } = useParticipantStatus(participantName, port, {
    // TODO remove isRemote check when BE ready with endpoint for remote participant
    enabled: isRemote ? false : phase === ParticipantDto.phase.RUNNING,
  });
  const [userToRemove, setUserToRemove] = useState<null | string>(null);
  const { data: users, isFetching: isUsersFetching } = useGetUsers(
    participantName,
    {
      // TODO remove isRemote check when BE ready with endpoint for remote participant
      enabled: !!healthStatus?.active || isRemote,
      retryDelay: 3 * 1000,
      retry: 3,
    },
    isRemote,
    apiVersion,
  );
  const { mutate: deleteUser, isLoading: isUserDeleting } = useDeleteUser(participantName, isRemote, apiVersion);
  const { onSuccessUserRemove } = useNotifications();
  const { isAdmin } = useRoles();

  const columns = useUsersTableColumns({ participantName, isRemote, setUserToRemove, apiVersion, validatorParent });

  const [columnVisibility, setColumnVisibility] = useState({
    actions: !!isAdmin,
  });

  const { filteredRows, onRowsFilteringChange } = useTableFilteringRows<UserDto>();

  const isPossibleToAddUser = useMemo(
    () => healthStatus?.connectedDomains?.length,
    [healthStatus?.connectedDomains?.length],
  );

  return users ? (
    <div className={cx(s.users, ui.container)}>
      <div className={ui.containerHeader}>
        <PageSubCaption isDataLoaded={!isUsersFetching} text="item" data={filteredRows ?? users} />

        {isAdmin && (
          <ButtonTooltip
            onClick={() =>
              navigate(userCreatePath(participantName, isRemote), {
                state: {
                  apiVersion,
                  isStandalone: !validatorParent,
                },
              })
            }
            disabled={!isPossibleToAddUser || isUsersFetching}
            text={isPossibleToAddUser ? '' : Labels.CONNECT_PARTICIPANT_TO_DOMAIN_FIRST}
          >
            <Icon icon={Icon.icons.add} />
            Create user
          </ButtonTooltip>
        )}
      </div>

      <Table
        data={users ?? []}
        columns={columns}
        wrapperClassName={ui.tableWrapperNoPadding}
        isSearchable
        isSortable
        isPaginated
        isFetching={isUsersFetching}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        onRowsFilteringChange={onRowsFilteringChange}
      />

      {userToRemove && (
        <Modal
          withConfirm
          onSubmit={() => {
            deleteUser(userToRemove, {
              onSuccess: () => onSuccessUserRemove(userToRemove),
              onSettled: () => setUserToRemove(null),
            });
          }}
          onClose={() => setUserToRemove(null)}
          title="Remove user?"
          text={`Are you sure you want to remove ${userToRemove} ?`}
          isLoading={isUserDeleting}
          submitButtonProps={{ type: 'button' }}
          submitText="Confirm"
        />
      )}
    </div>
  ) : (
    <TableSkeleton />
  );
};

export * from '../types';
